import {
  AddClientRequest,
  AddClientResponse,
  ClientData,
  ClientStatus,
  CreateAccountFromInviteRequest,
  DisplayResultMessage,
  EditClientRequest,
  ExternalInvitationType,
  GetWhitelistIpsRequest,
  LanguageNameSpaces,
  OpenConfirmModal,
  UsageType,
} from "@shared/types";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  PRIMARY_EXCHANGES,
  QUICK_CONNECT_EXCHANGES,
} from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import {
  getCreateOrEditAccountErrorMessage,
  getEditAccountSuccessMessage,
} from "@shared/features/onboarding/utils";
import { UseQuotaModal } from "@shared/features/settings/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { useClientsData, useUser } from "@shared/utils/hooks/misc";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { useSelector } from "react-redux";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import {
  getErrorStatus,
  refreshAndPersistToken,
} from "@shared/services/baseQuery";

interface useClientFormProps {
  account?: ClientData;
  primaryExchange?: string;
  onSubmitSuccess?: () => void;
  closeDrawer?: () => void;
  displayResultMessage: DisplayResultMessage;
  useWhitelistIPs: (
    exchange?: string,
    args?: GetWhitelistIpsRequest
  ) => {
    whitelistIPs: ReactNode;
    checkAllowSave: () => boolean;
  };
  onConnectToExchangeClicked?: () => void;
  getErrorNotification?: (description: string, title?: string) => void;
  useQuotaModal: UseQuotaModal;
  openConfirmModal: OpenConfirmModal;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  isOnboarding?: boolean;
  isWhop?: boolean;
}

const useSharedClientForm = ({
  account,
  primaryExchange,
  onSubmitSuccess,
  closeDrawer,
  displayResultMessage,
  useWhitelistIPs,
  onConnectToExchangeClicked,
  getErrorNotification,
  useQuotaModal,
  openConfirmModal,
  getSuccessMessage,
  isOnboarding,
  isWhop,
}: useClientFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { getActiveAccountsWithSameApis } = useClientsData() || {};
  const { getIsFree } = useContext(ExchangesMetadataContext) || {};
  const {
    isExternalConnectAccount,
    token: externalInviteToken,
    hasReadAccess,
    inviteData,
  } = useContext(ExternalConnectAccountContext) || {};
  const {
    setTab,
    exchangesData,
    exchangesLoading,
    isDemoWorkspace,
    exchangeToClientName,
    handleOnSubmit,
    accountName,
    accountNameError,
    selectedSubExchanges,
    getAccountSubExchange,
    addClientSucceeded,
    setAddClientSucceeded,
    setCreatedAccounts,
  } = useContext(AccountFormContext)!;
  const isEdit = account !== undefined;
  const [showPassphrase, setShowPassphrase] = useState(false);
  const { user, isFetching: userIsFetching } = useUser();
  const userAuthToken = useSelector(
    sharedLib.selectors.authentication.selectToken
  );

  const getWhitelistIpsArgs = useMemo(() => {
    if (!account?.client_id && !externalInviteToken) return undefined;
    let request: GetWhitelistIpsRequest = {};
    if (account?.client_id) request.client_id = account.client_id;
    if (externalInviteToken) request.authToken = externalInviteToken;
    if (externalInviteToken && hasReadAccess && userAuthToken)
      request.user_auth_token = userAuthToken;
    return request;
  }, [account?.client_id, externalInviteToken, hasReadAccess, userAuthToken]);
  const { isFetching: isFetchingWhitelistIPs } =
    sharedLib.sharedApi.configuration.useGetWhitelistIPsQuery(
      getWhitelistIpsArgs
    );
  const [checkApiKeyExistence, checkApiKeyExistenceStatus] =
    sharedLib.sharedApi.configuration.useLazyCheckApiKeyExistenceQuery();
  const [addClient, addClientStatus] =
    sharedLib.sharedApi.configuration.useAddClientMutation();
  const [createAccountFromInvite, createAccountFromInviteStatus] =
    sharedLib.sharedApi.configuration.useCreateAccountFromInviteMutation();
  const [editClient, editClientStatus] =
    sharedLib.sharedApi.configuration.useEditClientMutation();

  const { whitelistIPs, checkAllowSave } = useWhitelistIPs(
    primaryExchange,
    getWhitelistIpsArgs
  );
  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuotaCheck,
  } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });

  const isQuickConnectExchange =
    !!primaryExchange && QUICK_CONNECT_EXCHANGES.has(primaryExchange);
  const isHyperLiquid = primaryExchange === PRIMARY_EXCHANGES.HYPERLIQUID;

  useEffect(() => {
    if (primaryExchange) {
      handleCheckShowPassphrase(primaryExchange);
    }
  }, [primaryExchange, exchangesData]);

  const isLoadingSaveClient =
    addClientStatus.isLoading ||
    createAccountFromInviteStatus.isLoading ||
    editClientStatus.isLoading ||
    checkApiKeyExistenceStatus.isFetching ||
    userIsFetching;

  const handleCheckShowPassphrase = (exchange: string) => {
    const selectedExchange = exchangesData?.find(
      (exchangeInfo) => exchangeInfo.nested_exchange === exchange
    );
    setShowPassphrase(!!selectedExchange && !!selectedExchange.api_passphrase);
  };

  const createAccountFromInviteWithTokenRefresh = async (
    request: CreateAccountFromInviteRequest
  ): Promise<AddClientResponse> => {
    try {
      return await createAccountFromInvite(request).unwrap();
    } catch (error) {
      if (getErrorStatus(error) !== 401 || !request.user_auth_token) {
        throw error;
      }
      const updatedToken = await refreshAndPersistToken(false, isWhop);
      if (!updatedToken) throw error;
      return await createAccountFromInvite({
        ...request,
        user_auth_token: updatedToken,
      }).unwrap();
    }
  };

  useEffect(() => {
    if (addClientSucceeded && !!user && !userIsFetching) {
      onSubmitSuccess?.();
      closeDrawer?.();
    }
  }, [addClientSucceeded, user, userIsFetching]);

  const handleSaveClientData = (
    formValues: (AddClientRequest | EditClientRequest) & { exchange: string }
  ) => {
    const errorMessage = getCreateOrEditAccountErrorMessage(
      isEdit ? "edit" : "create",
      tCommon
    );

    if (!isEdit) {
      let promise: Promise<AddClientResponse>;
      if (isExternalConnectAccount) {
        const addClientRequest: CreateAccountFromInviteRequest = {
          ...formValues,
          authToken: externalInviteToken!,
        };
        if (isExternalConnectAccount && hasReadAccess) {
          addClientRequest.user_auth_token = userAuthToken;
        }
        if (inviteData?.type === ExternalInvitationType.PERSONAL) {
          addClientRequest.exchange_to_client_name = exchangeToClientName;
        } else {
          addClientRequest.exchanges = selectedSubExchanges?.map(
            (exchange) => exchange.exchange
          );
        }
        promise = createAccountFromInviteWithTokenRefresh(addClientRequest);
      } else {
        const addClientRequest = {
          ...formValues,
          exchange_to_client_name: exchangeToClientName,
          is_demo_query: isDemoWorkspace,
        };
        promise = addClient(addClientRequest).unwrap();
      }
      onConnectToExchangeClicked?.();
      displayResultMessage(
        promise,
        undefined,
        errorMessage,
        undefined,
        undefined,
        [400],
        true,
        true
      )
        .then((response) => {
          setCreatedAccounts(response.data.data);
          if (!isExternalConnectAccount) getSuccessMessage?.(response);
          if (isOnboarding && !isExternalConnectAccount) {
            setAddClientSucceeded(true);
          } else {
            closeDrawer?.();
            onSubmitSuccess?.();
          }
        })
        .catch((response) => {
          if (response.data?.code === 400)
            getErrorNotification?.(
              response.data.message,
              response.data.title ??
                tCommon("drawers.account_messages.create_account_error_title")
            );
        });
    } else {
      let { exchange, ...editClientRequest } = formValues;
      editClientRequest = {
        ...editClientRequest,
        client_id: account?.client_id!,
        client_name: accountName,
      };
      const successMessage = getEditAccountSuccessMessage(exchange, tCommon);
      displayResultMessage(
        editClient(editClientRequest as EditClientRequest).unwrap(),
        successMessage,
        errorMessage,
        10,
        () => {
          onSubmitSuccess?.();
          closeDrawer?.();
        }
      ).catch(() => {});
    }
  };

  const onFinish = async (
    formValues: (AddClientRequest | EditClientRequest) & { exchange: string }
  ) => {
    if (!checkAllowSave() && !isDemoWorkspace) return;

    try {
      let shouldCheckQuota: boolean = false;
      if (!isExternalConnectAccount) {
        const accountsWithSameApis = account
          ? getActiveAccountsWithSameApis?.(account.client_id)
          : undefined;
        const hasAccountsWithSameApisDifferentUuid = accountsWithSameApis?.some(
          (otherAccount) =>
            account?.client_unique_identifier !==
            otherAccount.client_unique_identifier
        );
        if (
          formValues.api_key &&
          (!isEdit ||
            account?.client_status !== ClientStatus.ACTIVE ||
            hasAccountsWithSameApisDifferentUuid)
        ) {
          shouldCheckQuota = !(await checkApiKeyExistence({
            api_key: formValues.api_key,
          }).unwrap());
        }
      }
      let isQuotaError: boolean | undefined | void;
      if (shouldCheckQuota && !getIsFree?.(primaryExchange)) {
        isQuotaError = await handleTriggerClick();
      }
      if (!isQuotaError) {
        handleOnSubmit(
          primaryExchange,
          () => handleSaveClientData(formValues),
          openConfirmModal
        );
      }
    } catch {}
  };

  const apiFieldNames: ApiFieldName[] = ["api_key", "api_secret"];
  if (showPassphrase) {
    apiFieldNames.push("api_passphrase");
  }

  const validateApiInfo = (
    formValues: any,
    fieldName: ApiFieldName,
    fieldLabel: string
  ) => {
    const fieldValue = formValues[fieldName];
    if (!account && !fieldValue) {
      return t("connect_form.please_provide", { fieldLabel });
    }

    const restApiFieldNames = apiFieldNames.filter(
      (name) => name !== fieldName
    );
    if (
      account &&
      !fieldValue &&
      restApiFieldNames.some((name) => formValues[name])
    ) {
      return t("connect_form.partial_api_error");
    }
  };

  const mainButtonData = {
    text: isEdit
      ? t("connect_form.update")
      : isDemoWorkspace
      ? t("connect_form.create_demo_account")
      : t("connect_form.connect_to", { exchange: primaryExchange }),
    loading: isLoadingSaveClient || isLoadingQuotaCheck,
    disabled: accountNameError || (!isEdit && !selectedSubExchanges?.length),
  };

  const secondaryButtonData = {
    text:
      isQuickConnectExchange &&
      !isEdit &&
      !isDemoWorkspace &&
      !isExternalConnectAccount
        ? t("connect_form.use_quick_connect", { exchange: primaryExchange })
        : undefined,
    onClick: () => setTab(ConnectExchangeTabs.QUICK_CONNECT),
  };

  const showChangeAccountTypeMessage = !isEdit;

  const accountSubExchange = useMemo(
    () => getAccountSubExchange(account),
    [account]
  );

  return {
    isFetchingWhitelistIPs,
    isQuickConnectExchange,
    primaryExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    validateApiInfo,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    showChangeAccountTypeMessage,
    exchangesLoading,
    accountSubExchange,
    isHyperLiquid,
  };
};

export default useSharedClientForm;

export type ApiFieldName = "api_key" | "api_secret" | "api_passphrase";
